import { Eye, Headphones, ScanFace, Waves, Wind } from "lucide-react";
import type { ComponentType } from "react";
import type { LayerId } from "../content/sneeze";

interface LayerPanelProps {
  layers: Record<LayerId, boolean>;
  surfaceOpacity: number;
  onToggle: (layer: LayerId) => void;
  onOpacityChange: (opacity: number) => void;
}

const layerRows: Array<{
  id: LayerId;
  label: string;
  meta: string;
  Icon: ComponentType<{ size?: number; strokeWidth?: number }>;
}> = [
  { id: "surface", label: "Surface", meta: "NIH", Icon: ScanFace },
  { id: "respiratory", label: "Lungs + trachea", meta: "NIH", Icon: Wind },
  { id: "larynx", label: "Larynx", meta: "NIH", Icon: Headphones },
  { id: "mouth", label: "Oral cavity", meta: "NIH", Icon: Waves },
  { id: "ocular", label: "Eye detail", meta: "NIH", Icon: Eye },
  { id: "nervous", label: "Neural signal", meta: "Overlay", Icon: Waves },
  { id: "pressure", label: "Pressure + flow", meta: "Overlay", Icon: Wind },
];

export function LayerPanel({ layers, surfaceOpacity, onToggle, onOpacityChange }: LayerPanelProps) {
  return (
    <section className="layer-panel panel" aria-labelledby="layer-panel-title">
      <div className="panel__label" id="layer-panel-title">
        Visible systems
      </div>
      <div className="layer-list">
        {layerRows.map(({ id, label, meta, Icon }) => (
          <div className="layer-row" key={id}>
            <button
              type="button"
              className="layer-row__button"
              onClick={() => onToggle(id)}
              aria-pressed={layers[id]}
            >
              <Icon size={14} strokeWidth={1.7} />
              <span>{label}</span>
              <small>{meta}</small>
              <span className={`switch${layers[id] ? " is-on" : ""}`} aria-hidden="true" />
            </button>
            {id === "surface" && layers.surface && (
              <label className="opacity-control">
                <span>Opacity</span>
                <input
                  type="range"
                  min="0.05"
                  max="0.72"
                  step="0.01"
                  value={surfaceOpacity}
                  onChange={(event) => onOpacityChange(Number(event.currentTarget.value))}
                />
                <output>{Math.round(surfaceOpacity * 100)}%</output>
              </label>
            )}
          </div>
        ))}
      </div>
      <p className="layer-panel__note">
        NIH geometry is labeled <b>NIH</b>. Motion fields and neural paths are explanatory overlays.
      </p>
    </section>
  );
}
